/* eslint-disable @typescript-eslint/no-magic-numbers */
import { defaultPropertyDefinitions } from "@buildwithflux/constants";
import { SymbolHelpers } from "../../../../imports/common/SymbolHelpers";
export class KiCadHelpers {
    linearUnitConversionFactor;
    angularUnitConversionFactor = 10;
    spreadXTerminalPositionOutBy = 1.5;
    spreadYTerminalPositionOutBy = 4;
    constructor(version) {
        switch (version) {
            case "v4":
                this.linearUnitConversionFactor = 7;
                break;
            case "v5":
                this.linearUnitConversionFactor = 0.1778;
                break;
        }
    }
    static getPropertyBySystemKey(defaultPropertyKey, element) {
        const partProperties = Object.values(element.part_version_data_cache.properties);
        const elementProperties = Object.values(element.properties);
        const allProperties = partProperties.concat(elementProperties);
        const defaultProperties = Object.values(defaultPropertyDefinitions);
        const systemDefaultProperty = defaultProperties.find((defaultProp) => defaultProp.key === defaultPropertyKey);
        return allProperties.find((property) => property.name === systemDefaultProperty?.label);
    }
    static extractStringFromDoubleQuotes(textWithQuote) {
        return textWithQuote?.match(/(?:"[^"]*"|^[^"]*$)/)?.[0]?.replace(/"/g, "") || "";
    }
    static extractScale(input) {
        // Remove the comma from the string
        const cleanedString = input.replace(",", ".");
        // Convert the cleaned string to a number
        const result = parseFloat(cleanedString);
        // Check if the result is a valid number
        if (isNaN(result)) {
            throw new Error("Invalid input string");
        }
        return result;
    }
    static degreesToRadians(degrees) {
        return degrees * (Math.PI / 180);
    }
    // Determine the center point of an arc circle given the start, end and midpoint
    static calculateCenterPointOfArc(start, mid, end) {
        // Calculate the perpendicular bisectors of two chords: start-mid and mid-end
        // Midpoints of the chords
        const midPoint1 = { x: (start.x + mid.x) / 2, y: (start.y + mid.y) / 2 };
        const midPoint2 = { x: (mid.x + end.x) / 2, y: (mid.y + end.y) / 2 };
        // Slopes of the chords
        const slope1 = (mid.y - start.y) / (mid.x - start.x);
        const slope2 = (end.y - mid.y) / (end.x - mid.x);
        // Perpendicular bisector slopes
        const perpSlope1 = -1 / slope1;
        const perpSlope2 = -1 / slope2;
        // Equations of the perpendicular bisectors:
        // y = perpSlope1 * (x - midPoint1.x) + midPoint1.y
        // y = perpSlope2 * (x - midPoint2.x) + midPoint2.y
        // Solving for the intersection point of the two bisectors (center of the circle)
        const centerX = (perpSlope1 * midPoint1.x - perpSlope2 * midPoint2.x + midPoint2.y - midPoint1.y) /
            (perpSlope1 - perpSlope2);
        const centerY = perpSlope1 * (centerX - midPoint1.x) + midPoint1.y;
        return { x: centerX, y: centerY };
    }
    static cleanupDatasheetUrl(rawDatasheetUrl) {
        const cleanedDatasheetUrl = rawDatasheetUrl?.replaceAll("~", "").trim();
        if (cleanedDatasheetUrl && this.isUrl(cleanedDatasheetUrl)) {
            return cleanedDatasheetUrl;
        }
    }
    static getPackageCodeFromFootprint(footprint) {
        if (footprint?.includes(":")) {
            return footprint.split(":")[1]?.trim();
        }
        return footprint?.trim();
    }
    static getPartTypeFromFootprint(footprint) {
        if (footprint?.includes(":")) {
            const partType = footprint.split(":")[0];
            if (partType && !partType.toLowerCase().startsWith("package_")) {
                return partType.replaceAll("_", " ").trim();
            }
        }
    }
    static normalizePartType(rawPartType) {
        // Now match it with our build in types to verify it
        return (defaultPropertyDefinitions.part_type.auto_complete_options?.find((partType) => {
            // Escape all special characters (eg. "Resistor" -> "Resistor\.")
            const possibleMatches = partType.replace(/[\\^$*+?.()|[\]{}]/g, "\\$&");
            const regex = new RegExp(`(${possibleMatches})s?`, "i");
            return regex.test(rawPartType || "");
        }) || rawPartType);
    }
    static isUrl(str) {
        try {
            const url = new URL(str);
            return (url.protocol === "http:" || url.protocol === "https:") && url.hostname !== "";
        }
        catch {
            return false;
        }
    }
    spreadXTerminalPosition(xPosition) {
        return Math.round(xPosition * this.spreadXTerminalPositionOutBy);
    }
    spreadYTerminalPosition(yPosition) {
        return Math.round(yPosition * this.spreadYTerminalPositionOutBy);
    }
    fromKiCadLinearUnits(kiCadUnits) {
        // See: https://en.wikibooks.org/wiki/Kicad/file_formats#Units
        return Math.round(kiCadUnits / this.linearUnitConversionFactor);
    }
    fromKiCadAngularUnits(kiCadAngle) {
        // See: https://en.wikibooks.org/wiki/Kicad/file_formats#Units
        return Math.round(kiCadAngle / this.angularUnitConversionFactor);
    }
    kicadSchematicPositionToFluxSchematicPosition(kicadSchematicPosition) {
        return {
            x: this.fromKiCadLinearUnits(kicadSchematicPosition.x),
            y: SymbolHelpers.invertAxis(this.fromKiCadLinearUnits(kicadSchematicPosition.y)),
        };
    }
}
//# sourceMappingURL=KiCadHelpers.js.map